import { Link } from 'react-router-dom';
import { DollarSign, ShoppingCart, TrendingUp, Truck, Clock, Package } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KpiCard } from '@/components/KpiCard';
import { OrderStatusBadge } from '@/components/OrderStatusBadge';
import { Loading, ErrorState } from '@/components/ui/states';
import { Table, THead, TBody, TR, TH, TD, EmptyRow } from '@/components/ui/table';
import { useDashboard, useRevenue, useOrders } from '@/api/hooks';
import { formatUsdt, formatDateTime } from '@/lib/utils';

export function DashboardPage() {
  const stats = useDashboard();
  const revenue = useRevenue(30);
  const recent = useOrders({ page: 1, pageSize: 8 });

  if (stats.isLoading) return <Loading />;
  if (stats.isError) return <ErrorState error={stats.error} onRetry={() => stats.refetch()} />;

  const s = stats.data!;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Revenue today" value={`${formatUsdt(s.todayRevenueUsdt)} USDT`} icon={DollarSign} />
        <KpiCard label="Orders today" value={String(s.todayOrders)} icon={ShoppingCart} />
        <KpiCard label="Profit today" value={`${formatUsdt(s.todayProfitUsdt)} USDT`} icon={TrendingUp} accent="success" />
        <KpiCard label="Active suppliers" value={String(s.activeSuppliers)} icon={Truck} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Pending orders" value={String(s.pendingOrders)} icon={Clock} accent="warning" />
        <KpiCard label="Products" value={String(s.totalProducts)} icon={Package} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue — last 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          {revenue.isLoading ? (
            <Loading />
          ) : revenue.isError ? (
            <ErrorState error={revenue.error} onRetry={() => revenue.refetch()} />
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenue.data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d: string) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={48} />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Line type="monotone" dataKey="revenueUsdt" name="Revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="profitUsdt" name="Profit" stroke="hsl(var(--success))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent orders</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <THead>
              <TR>
                <TH>Date</TH>
                <TH>Product</TH>
                <TH>User</TH>
                <TH>Total</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {recent.isLoading ? (
                <EmptyRow colSpan={5}>Loading…</EmptyRow>
              ) : recent.data && recent.data.items.length > 0 ? (
                recent.data.items.map((o) => (
                  <TR key={o.id}>
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDateTime(o.createdAt)}</TD>
                    <TD>
                      <Link className="hover:underline" to={`/products/${o.productId}`}>
                        {o.productName}
                      </Link>
                    </TD>
                    <TD className="text-muted-foreground">{o.telegramUsername ? `@${o.telegramUsername}` : o.telegramUserId}</TD>
                    <TD>{formatUsdt(o.sellingPriceUsdt)} USDT</TD>
                    <TD>
                      <OrderStatusBadge status={o.status} />
                    </TD>
                  </TR>
                ))
              ) : (
                <EmptyRow colSpan={5}>No orders yet.</EmptyRow>
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
